import type { BUITheme } from '@blofin/blofin-ui';
import EmptyDoc from '@blofin/blofin-component/packages/Common/EmptyDoc';
import PageLayout from 'components/layout/PageLayout';
import Breadcrumb from 'components/common/Breadcrumb';
import ArticleFeedback from 'components/shared/ArticleFeedback';
import ListPageHeader from 'components/shared/ListPageHeader';
import RawHTML from 'components/shared/RawHTML';
import ShareButton from 'components/shared/ShareButton';
import SocialLinks from 'components/shared/SocialLinks';
import type { GetServerSideProps } from 'next';
import { useTranslations } from 'next-intl';
import Head from 'next/head';
import { getArticleDetail } from 'services/help-center';
import { detailVOToArticleDetail, parseArticleId } from 'services/help-center/utils';
import { requireMessages } from 'services/http/requireMessages';
import type { ArticleDetail } from 'types/help-center';
import BlofinNative from 'sdk';

interface TermsDetailPageProps {
  theme: BUITheme;
  article: ArticleDetail | null;
  messages: Record<string, unknown>;
  isApp: boolean;
}

export const getServerSideProps: GetServerSideProps<TermsDetailPageProps> = async (ctx) => {
  const cookies = ctx.req.headers.cookie || '';
  const themeMatch = cookies.match(/(?:^|;\s*)theme=(light|dark)/);
  const theme = (themeMatch?.[1] as BUITheme) || 'light';

  const isApp = BlofinNative.isApp(ctx.req.headers['user-agent'] || '');

  const articleId = parseArticleId(ctx.params?.id as string);
  const language = ctx.locale || 'en';
  const previewParam = typeof ctx.query.preview === 'string' ? ctx.query.preview : undefined;
  const signParam = typeof ctx.query.sign === 'string' ? ctx.query.sign : undefined;

  let article: ArticleDetail | null = null;
  let messages: Record<string, unknown> = {};

  try {
    const [detailResult, messagesResult] = await Promise.allSettled([
      !isNaN(articleId)
        ? getArticleDetail(articleId, language, { preview: previewParam, sign: signParam })
        : Promise.resolve(null),
      requireMessages(['common', 'components', 'support'], ctx.locale),
    ]);

    if (messagesResult.status === 'fulfilled') {
      messages = messagesResult.value;
    }

    if (detailResult.status === 'fulfilled' && detailResult.value?.code === 200) {
      const detail = detailResult.value.data;
      if (detail) {
        article = detailVOToArticleDetail(detail);
      }
    }
  } catch (error) {
    console.log('[TermsDetail] getServerSideProps error:', error);
  }

  return { props: { theme, article, messages, isApp } };
};

export default function TermsDetailPage({ theme, article, isApp }: TermsDetailPageProps) {
  const t = useTranslations('support');

  if (!article) {
    return (
      <PageLayout theme={theme} showTheme hideHeader={isApp} hideFooter={isApp}>
        <section className="-mt-16 w-full bg-light-background px-4 pb-10 pt-[88px] dark:bg-base-fill-primary pad:px-8 pad:pb-20 pad:pt-[104px]">
          <div className="mx-auto flex w-full min-w-0 max-w-[1200px] flex-col gap-4 pad:gap-10">
            <Breadcrumb
              items={[
                { label: t('helpCenter'), href: '/support' },
                { label: t('termsTitle'), href: '/support/Terms' },
              ]}
            />
            <ListPageHeader title={t('termsTitle')} />
            <div className="flex items-center justify-center border-t border-base-line-primary py-20 max-pad:border-t-0">
              <EmptyDoc emptyText={t('articleNotFound')} />
            </div>
          </div>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout theme={theme} showTheme hideHeader={isApp} hideFooter={isApp}>
      <Head>
        <title>{`${article.title ?? ''} - ${t('seoTermsSuffix')}`}</title>
        <meta name="description" content={article.title} />
      </Head>
      <section className="-mt-16 w-full bg-light-background px-4 pb-10 pt-[88px] dark:bg-base-fill-primary pad:px-8 pad:pb-20 pad:pt-[104px]">
        <div className="mx-auto flex w-full min-w-0 max-w-[1200px] flex-col gap-4 pad:gap-10">
          <Breadcrumb
            items={[
              { label: t('helpCenter'), href: '/support' },
              { label: t('termsTitle'), href: '/support/Terms' },
              { label: article.title },
            ]}
          />

          <ListPageHeader title={t('termsTitle')} />

          <div className="flex flex-col gap-6 border-t border-base-line-primary pt-6 max-pad:border-t-0">
            <div className="flex flex-col gap-4 pad:flex-row pad:items-start pad:justify-between pad:gap-4">
              <h1 className="text-[22px] font-bold leading-8 text-base-label pad:text-[32px] pad:leading-10">
                {article.title}
              </h1>
              <ShareButton />
            </div>
            <span className="text-sm leading-[18px] text-base-label-40 pad:text-base pad:leading-5">
              {article.date}
            </span>

            <RawHTML html={article.content} className="text-base-label" />

            {/* <SocialLinks /> */}

            <ArticleFeedback />
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
